import { FC, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import MapboxLanguage from '@mapbox/mapbox-gl-language';
import { useTranslation } from 'react-i18next';

import 'mapbox-gl/dist/mapbox-gl.css';

import { MODAL_ID } from 'constants/settings';
import { MAPBOX_REVERSE_GEOCODING_URL, mapboxFeatureToOption } from 'utils/mapbox';

import styles from './styles.module.scss';
import { Address, Option } from '@/types/mapbox';

const MAPBOX_ACCESS_TOKEN = import.meta.env.VITE_MAP_API_TOKEN;
const DEFAULT_CENTER = {
  lat: Number(import.meta.env.VITE_DEFAULT_MAP_LAT) || 53.902472,
  ln: Number(import.meta.env.VITE_DEFAULT_MAP_LN) || 27.561823,
};

type MapStyleType = 'streets' | 'hybrid';

const MAP_STYLES: Record<MapStyleType, string> = {
  streets: import.meta.env.VITE_MAPBOX_STYLE || 'mapbox://styles/mapbox/streets-v12',
  hybrid: import.meta.env.VITE_MAPBOX_HYBRID_STYLE || 'mapbox://styles/mapbox/satellite-streets-v12',
};

interface MapPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (option: Option<Address>) => void;
  initialValue: Option<Address> | Address | null;
  lang: string;
  labels: any;
}

const MapPickerModal: FC<MapPickerModalProps> = ({ isOpen, onClose, onSelect, initialValue, lang, labels }) => {
  const { t } = useTranslation();
  const mapContainerRef = useRef(null);
  const mapRef = useRef<mapboxgl.Map>(null);
  const markerRef = useRef(null);
  const [selection, setSelection] = useState(initialValue || null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [mapStyle, setMapStyle] = useState<MapStyleType>('streets');

  const modalRoot = typeof document === 'undefined' ? null : document.getElementById(MODAL_ID);
  const reverseAbortRef = useRef<AbortController>(null);
  const initialFetcherAbortRef = useRef(null);

  const removeMarker = () => {
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
  };

  useEffect(() => {
    if (isOpen) {
      setSelection(initialValue || null);
      if (!initialValue) {
        removeMarker();
      }
    }
  }, [isOpen, initialValue]);

  const cleanupMap = () => {
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
    markerRef.current = null;
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = '';
        cleanupMap();
        if (reverseAbortRef.current) {
          reverseAbortRef.current.abort();
        }
        if (initialFetcherAbortRef.current) {
          initialFetcherAbortRef.current.abort();
        }
      };
    }

    return undefined;
  }, [isOpen]);

  const placeMarker = useCallback((lng: number, lat: number) => {
    if (!mapRef.current) {
      return;
    }
    if (!markerRef.current) {
      markerRef.current = new mapboxgl.Marker({
        color: '#FF0000',
        draggable: false,
      })
        .setLngLat([lng, lat])
        .addTo(mapRef.current);
    } else {
      markerRef.current.setLngLat([lng, lat]);
    }
  }, []);

  const handleReverseGeocoding = useCallback(
    (lng: number, lat: number) => {
      if (MAPBOX_ACCESS_TOKEN) {
        setIsLoading(true);
        setErrorMessage('');

        if (reverseAbortRef.current) {
          reverseAbortRef.current.abort();
        }
        reverseAbortRef.current = new AbortController();

        const params = new URLSearchParams({
          longitude: String(lng),
          latitude: String(lat),
          access_token: MAPBOX_ACCESS_TOKEN,
          language: lang,
          limit: '1',
        });

        fetch(`${MAPBOX_REVERSE_GEOCODING_URL}?${params.toString()}`, {
          signal: reverseAbortRef.current.signal,
        })
          .then((response) => response.json())
          .then((result) => {
            if (result?.features?.length) {
              const option = mapboxFeatureToOption(result.features[0]);
              setSelection(option);
              placeMarker(lng, lat);
            } else {
              setErrorMessage(labels.error);
            }
          })
          .catch(() => {
            setErrorMessage(labels.error);
          })
          .finally(() => {
            reverseAbortRef.current = null;
            setIsLoading(false);
          });
        return;
      }

      setErrorMessage(labels.error);
    },
    [lang, labels.error, placeMarker],
  );

  const handleMapClick = useCallback(
    (event: mapboxgl.MapMouseEvent & mapboxgl.EventData) => {
      const lngLat = event?.lngLat;
      if (!lngLat) {
        return;
      }
      const { lng, lat } = lngLat;
      if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
        return;
      }
      handleReverseGeocoding(lng, lat);
    },
    [handleReverseGeocoding],
  );

  useEffect(() => {
    if (isOpen && modalRoot && MAPBOX_ACCESS_TOKEN) {
      mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
      const location = initialValue?.value?.location || initialValue?.location;
      const centerLng = location?.longitude ?? DEFAULT_CENTER.ln;
      const centerLat = location?.latitude ?? DEFAULT_CENTER.lat;

      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: MAP_STYLES[mapStyle],
        center: [centerLng, centerLat],
        zoom: location ? 8 : 4.5,
        attributionControl: false,
        logoPosition: 'bottom-right',
      });

      mapRef.current.addControl(new MapboxLanguage({ defaultLanguage: 'ru' }));

      mapRef.current.on('click', handleMapClick);

      if (location) {
        const markerLng = location?.longitude;
        const markerLat = location?.latitude;
        if (Number.isFinite(markerLng) && Number.isFinite(markerLat)) {
          placeMarker(markerLng, markerLat);
        }
      }

      return () => {
        if (mapRef.current) {
          mapRef.current.off('click', handleMapClick);
        }
      };
    }

    return undefined;
  }, [handleMapClick, initialValue, isOpen, modalRoot, placeMarker]);

  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setStyle(MAP_STYLES[mapStyle]);
  }, [mapStyle]);

  useEffect(() => {
    if (!isOpen || !mapRef.current) {
      return;
    }

    if (!selection?.value?.location) {
      removeMarker();
      mapRef.current.flyTo({
        center: [DEFAULT_CENTER.ln, DEFAULT_CENTER.lat],
        zoom: 4.5,
        essential: true,
      });
      return;
    }

    const lng = selection.value.location.longitude;
    const lat = selection.value.location.latitude;

    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }
    const currentZoom = mapRef.current.getZoom();

    mapRef.current.flyTo({
      center: [lng, lat],
      ...(currentZoom < 8 && { zoom: 8 }),
      essential: true,
    });
    placeMarker(lng, lat);
  }, [isOpen, placeMarker, selection]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  const handleConfirm = () => {
    if (selection) {
      onSelect(selection);
    }
  };

  if (!isOpen || !modalRoot) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <header className={styles.header}>
          <h3>{labels.title}</h3>
          <button type="button" className={styles.closeButton} onClick={onClose}>
            ×
          </button>
        </header>
        <p className={styles.helper}>{labels.helper}</p>
        {MAPBOX_ACCESS_TOKEN ? (
          <div className={styles.mapWrapper}>
            <div className={styles.styleSwitcher}>
              <button
                type="button"
                className={`${styles.styleButton} ${mapStyle === 'streets' ? styles.active : ''}`}
                onClick={() => setMapStyle('streets')}
              >
                {t('map.streets')}
              </button>
              <button
                type="button"
                className={`${styles.styleButton} ${mapStyle === 'hybrid' ? styles.active : ''}`}
                onClick={() => setMapStyle('hybrid')}
              >
                {t('map.hybrid')}
              </button>
            </div>
            <div ref={mapContainerRef} className={styles.map} />
          </div>
        ) : (
          <div className={styles.error}>{labels.error}</div>
        )}
        {selection?.label && <p className={styles.selection}>{selection.label}</p>}
        {errorMessage && <p className={styles.error}>{errorMessage}</p>}
        <div className={styles.actions}>
          <button type="button" className={styles.secondaryButton} onClick={onClose}>
            {labels.cancel}
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={handleConfirm}
            disabled={!selection || isLoading}
          >
            {isLoading ? '…' : labels.confirm}
          </button>
        </div>
      </div>
    </div>,
    modalRoot,
  );
};

MapPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  lang: PropTypes.string,
  labels: PropTypes.shape({
    title: PropTypes.string.isRequired,
    helper: PropTypes.string.isRequired,
    confirm: PropTypes.string.isRequired,
    cancel: PropTypes.string.isRequired,
    error: PropTypes.string.isRequired,
  }).isRequired,
};

MapPickerModal.defaultProps = {
  initialValue: null,
  lang: 'ru',
};

export default MapPickerModal;
